import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Folder, Activity as ActivityIcon, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { authFetch } from '../lib/api';

const ClientHome = ({ user }) => {
  const [projects, setProjects] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchClientData();
  }, []);

  const fetchClientData = async () => {
    try {
      
      

      const [projectsRes, activitiesRes] = await Promise.all([
        authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects`),
        authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/activities?limit=10`)
      ]);

      if (projectsRes.ok) {
        const projectsData = await projectsRes.json();
        setProjects(projectsData.projects);
      }

      if (activitiesRes.ok) {
        const activitiesData = await activitiesRes.json();
        setActivities(activitiesData.activities);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-moonstone"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-up" data-testid="client-home">
      {/* Welcome */}
      <div>
        <h2 className="text-3xl font-semibold tracking-tight text-text-primary mb-2">Welcome, {user.name}!</h2>
        <p className="text-text-secondary">View your projects and track campaign performance</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="client-stats">
        <Card className="glass metric-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-brand uppercase tracking-wider">Your Projects</span>
              <Folder className="text-brand" size={24} />
            </div>
            <p className="text-3xl font-semibold tracking-tight text-text-primary">{projects.length}</p>
            <p className="text-sm text-positive mt-1">
              {projects.filter(p => p.status === 'Active').length} active
            </p>
          </CardContent>
        </Card>

        <Card className="glass metric-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-brand uppercase tracking-wider">Recent Updates</span>
              <ActivityIcon className="text-brand" size={24} />
            </div>
            <p className="text-3xl font-semibold tracking-tight text-text-primary">{activities.length}</p>
            <p className="text-sm text-text-secondary mt-1">This week</p>
          </CardContent>
        </Card>
      </div>

      {/* Projects */}
      <Card className="glass" data-testid="client-projects-list">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-brand">Your Projects</CardTitle>
          <Link
            to="/client/projects"
            className="text-sm text-brand hover:text-brand flex items-center gap-1"
          >
            View all
            <ArrowRight size={16} />
          </Link>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {projects.slice(0, 5).map((project) => (
              <Link
                key={project.id}
                to={`/project/${project.id}`}
                className="flex items-center justify-between p-4 rounded-xl bg-white/50 hover:bg-white/70 transition-all hover:-translate-y-1"
                data-testid={`client-project-${project.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-moonstone to-billiard flex items-center justify-center">
                    <Folder className="text-white" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-text-primary">{project.name}</h3>
                    <p className="text-sm text-text-secondary">Status: {project.status}</p>
                  </div>
                </div>
                <ArrowRight className="text-brand" size={20} />
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card className="glass" data-testid="client-activity">
        <CardHeader>
          <CardTitle className="text-brand">Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {activities.slice(0, 5).map((activity) => (
              <div
                key={activity.id}
                className="flex items-start gap-3 p-3 rounded-lg hover:bg-white/50 transition-colors"
              >
                <div className="w-2 h-2 rounded-full bg-moonstone mt-2" />
                <div className="flex-1">
                  <p className="text-sm text-text-primary">{activity.description}</p>
                  <p className="text-xs text-text-secondary mt-1">
                    {new Date(activity.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientHome;
